import Modal from '@Components/Shared/Modals/Modal';
import Button from '@mui/material/Button';
import translator from '@Components/Utilities/Translator';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useState, useEffect, useRef, useMemo } from 'react';
import LoadingRing from '@Components/MuiWrappers/LoadingRing';
import { bidderStatus, invalidCurrencyPopUpBaseCatIDs, gtmEventType, eventCategory } from '@Components/Utilities/SandhillsConstants';
import { loadable } from '@Components/Utilities/SandhillsLoadable';
import ready from 'document-ready';
import { getCookie } from '@Components/Utilities/CookieHelper';
import MultiFactorAuthenticationForm from '@Components/Forms/MultiFactorAuthentication/MultiFactorAuthenticationForm';
import { addAuctionSiteDLVs, gtmPushEvent, sandhillsRequest, sandhillsLogging } from '@Components/Utilities/SandhillsUtility';
import { placeAuctionTimeBid } from '@Components/Utilities/AuctionTimeBidding';
import { placeBidCallerBid } from '@Components/Utilities/BidCallerPreBidding';
import { addUpdateBAAcceptanceToSessionStorage } from '@Components/Utilities/SaleBidAgreement';

const PaymentAuthorizationForm = loadable(() => import('@Components/Forms/PaymentAuthorization/PaymentAuthorizationForm'));
const BiddingConfirmation = loadable(() => import('@Components/Listings/BiddingConfirmation'));

const BiddingAgreementModal = (props) => {   

    const {
        biddingAgreement,
        onClose,
        onBiddingAgreementDataUpdated,
        auctionBiddingData,
        resetAuctionData,
    } = props;

    // Making use of useMemo because otherwise as the AT updates come in this value could change and cause the users bid to change without their input
    const bidValue = useMemo(() => auctionBiddingData?.UserBid, []);

    const timeout = useRef(null);
    const [currentUser, setCurrentUser] = useRecoilState(atoms.currentUser);
    const [isLoading, setIsLoading] = useState(false);
    const [showMFAForm, setShowMFAForm] = useState(false);
    const [showPaymentAuthForm, setShowPaymentAuthForm] = useState(false);
    const [bidConfirmed, setBidConfirmed] = useState(false);
    const [autoOutbid, setAutoOutbid] = useState(false);
    const [tieBid, setTieBid] = useState(false);    
    const [bidErrorDetails, setBidErrorDetails] = useState(props.BidErrorDetails || {
        errorMessage: '',
        highlightedBidIncrement: 0,
        autoBidIncrementData: null
    });

    useEffect(() => {
        if (props.BidErrorDetails) {
            setBidErrorDetails(props.BidErrorDetails);
        }
    }, [props.BidErrorDetails]);
    
    const [notificationSettings, setNotificationSettings] = useState(null);
    const [firstTimeUserAcceptedAgreement, setfirstTimeUserAcceptedAgreement] = useState(false);
    const [showCurrencyPopUp, setShowCurrencyPopUp] = useState(false);
    const routes = useRecoilValue(atoms.routes);
    const currentSite = useRecoilValue(atoms.currentWebsite);

    // On AuctionTime we may need to display a warning if they enter an excessive bid amount
    const isLargeBid = useMemo(() => {
        if (!props.IsAT || !bidValue) {
            return false;
        }

        return bidValue >= (auctionBiddingData.CurrentBid * 10)
    }, []);
    const [largeBidConfirmChecked, setLargeBidConfirmChecked] = useState(false);
    const [isLargeBidConfirmed, setIsLargeBidConfirmed] = useState(false);

    useEffect(() => {
        //if we are accepting for the first time
        if (biddingAgreement?.BidAgreement?.UserAccepted) {
            //if event requires CC auth open form
            if ((biddingAgreement?.BidAgreement?.PreAuthRequired && biddingAgreement?.PaymentGateways?.length > 0 && !biddingAgreement.BidAgreement.UserIsApproved)) {
                setShowPaymentAuthForm(true);
            }
        }
    }, [biddingAgreement?.BidAgreement?.UserAccepted]);

    useEffect(() => {
        ready(() => {
            if (biddingAgreement.PaymentGateways && biddingAgreement.PaymentGateways.length > 0) {
                PaymentAuthorizationForm.preload();
            }            
        });
    }, []);

    useEffect(() => {

        if (biddingAgreement?.BidAgreement?.UserAccepted && biddingAgreement?.BidAgreement?.UserIsApproved && auctionBiddingData) {
            setShowPaymentAuthForm(false);
        }

    }, [biddingAgreement?.BidAgreement?.UserAccepted, biddingAgreement?.BidAgreement?.UserIsApproved]);

    const closeModal = () => {
        if (timeout.current) {
            clearTimeout(timeout.current);
        }
        onClose();
    };

    const closeModalTimer = () => setTimeout(() => {
        onClose();
    }, 3000);

    const autoCloseModal = () => {
        timeout.current = closeModalTimer();
    };

    const loginURL = routes.SSO + '?to=' + encodeURIComponent(window.location.href);

    const acceptAgreement = () => {
        if (!biddingAgreement.BidAgreement.UserAccepted && !isLoading) {
            if (!biddingAgreement.HasPassedMultiFactorAuthentication) {
                setIsLoading(true);
                sandhillsRequest({
                    url: routes.GetMultifactorAuthenticationValues,
                    method: 'post',
                    errorHandling: {
                        message: 'An error occurred while accepting the Terms and Conditions. Please refresh your page and try again.',
                        useConsole: false,
                        useAlert: true
                    }
                }).then((data) => {
                    setIsLoading(false);
                    biddingAgreement.MultifactorAuthenticationValues = data;                    
                    setShowMFAForm(true);
                })
                    .catch((error) => {
                        setIsLoading(false);
                    });
            }
            else {
                setIsLoading(true);

                sandhillsRequest({
                    url: routes.AcceptBiddingAgreement,
                    data: {
                        referenceID: biddingAgreement.BidAgreement.EventOrSaleID,
                        eventCategoryID: biddingAgreement.BidAgreement.EventCategoryID
                    },
                    method: 'post',
                    errorHandling: {
                        message: 'An error occurred while accepting the Terms and Conditions. Please refresh your page and try again.',
                        useConsole: false,
                        useAlert: true
                    }
                }).then((data) => {
                    setIsLoading(false);
                    setfirstTimeUserAcceptedAgreement(true);
                    if (data?.BidAgreement?.Success) {
                        onBiddingAgreementDataUpdated(data);
                    }
                    else {
                        alert('An error occurred while accepting the Terms and Conditions. Please refresh your page and try again.');
                    }
                })
                .catch((error) => {
                    setIsLoading(false);
                });
            }
        }
    };
    const checkForNotifications = () => {
        sandhillsRequest({
            url: routes.CheckNotificationSettings,
            method: 'post'
        }).then((data) => {
            if (data == '' || data == undefined || data == null) {
                setNotificationSettings(null)
            }
            else {
                setNotificationSettings(data)
                gtmPushEvent("alert-preferences-modal-popup")
            }
        })
    }

    const checkForCurrencyPopUp = (IsFirstUserBidOnListing) => {
        let hoursToAuction = NaN
        if (!props.IsAT) {
            hoursToAuction = (Date.parse(props.EventStartDateTime) - Date.now()) / (1000 * 60 * 60);
        }
        else {
            hoursToAuction = props.auctionBiddingData.TimeRemaining / 3600000; // convert milliseconds to hours
        }
        if (IsFirstUserBidOnListing && currentUser?.CountryIsUSA && props?.listingCountry == "USA" && hoursToAuction >= 24
             && !Object.values(invalidCurrencyPopUpBaseCatIDs).includes(props.auctionBiddingData.BaseCatID) && getCookie('CurrencyAlertsSnoozed') == null
        ) {
            setShowCurrencyPopUp(true);
        }
    }

    const showInvalidAutoBidIncrementError = () => {                
        if (props.AutoBidIncrementData) {
            let selectedRange = getBidIncrementToHighlight(props.AutoBidIncrementData);

            setBidErrorDetails({
                ...bidErrorDetails,
                errorMessage: translator.translate('sandhills.invalidbidforautobidincrementerrorshowranges'),
                autoBidIncrementData: props.AutoBidIncrementData,
                highlightedBidIncrement: selectedRange
            });
        } else {
            sandhillsRequest({
                url: routes.GetAutoBidIncrementData,
                data: {
                    groupID: props.auctionBiddingData.AutoBidIncrementGroupID
                },
                errorHandling: {
                    useConsole: true,
                    useAlert: false
                }
            }).then((response) => {            
                let selectedRange = getBidIncrementToHighlight(response);

                setBidErrorDetails({
                    ...bidErrorDetails,
                    errorMessage: translator.translate('sandhills.invalidbidforautobidincrementerrorshowranges'), 
                    autoBidIncrementData: response,
                    highlightedBidIncrement: selectedRange
                });
            });
        }
    }

    const getBidIncrementToHighlight = (ranges) => {
        if (auctionBiddingData.UserBid && ranges && ranges.AutoBidIncrementRanges.length > 0) {

            for (let i = 0; i < ranges.AutoBidIncrementRanges.length; i++) {
                if (auctionBiddingData.UserBid >= ranges.AutoBidIncrementRanges[i].MinimumBidValue && (auctionBiddingData.UserBid <= ranges.AutoBidIncrementRanges[i].MaxBidValue || ranges.AutoBidIncrementRanges[i].MaxBidValue == 79228162514264337593543950335)) {
                    return ranges.AutoBidIncrementRanges[i].BidIncrementValue;
                }
            }
        }
        return 0;
    }

    const submitBid = () => {
        if (biddingAgreement?.BidAgreement?.UserIsApproved && auctionBiddingData && !isLoading) {
            auctionBiddingData.UserBid = bidValue;
            setIsLoading(true);

            const bidPromise = props.IsAT ?
                placeAuctionTimeBid(routes, auctionBiddingData, false)
                :
                placeBidCallerBid(routes, auctionBiddingData);

            bidPromise.then((data) => {
                setIsLoading(false);
                if (data.Success) {
                    setBidErrorDetails({
                        ...bidErrorDetails, errorMessage: ''
                    });
                    if (currentSite?.Site?.IsBidcaller || currentSite?.Site?.IsAuctionTime) {
                        checkForNotifications();
                        checkForCurrencyPopUp(data.IsFirstUserBidOnListing);
                    }
                    const skipListingDataLookup = props.IsAT;   //Skip listing data lookup for AT listings because if on list page, this can end up removing all other listings from SignalR connection group
                    resetAuctionData?.(auctionBiddingData?.ListingID, data.AddedToWatchlist, data.UserBidInfo, skipListingDataLookup, (data.BidIncrement && data.BidIncrement != auctionBiddingData.Increment) ? data.BidIncrement : null, auctionBiddingData?.AuctionUpdateKey);
                    setBidConfirmed(true);
                    setAutoOutbid(data.AutoOutbid);
                    setTieBid(data.IsTieBid);

                    var value = auctionBiddingData.UserBid - (auctionBiddingData.CurrentBid ?? 0);
                    var bidType = props.IsAT ? gtmEventType.liveBid : gtmEventType.preBid;
                    let eventID = '';
                    let eventTitle = '';
                    
                    if (props.IsEF) {
                        eventID = auctionBiddingData?.EventID;
                        eventTitle = auctionBiddingData?.EventTitle;
                    } else {
                        eventID = props.AuctionID;
                        eventTitle = props.AuctionName;
                    }
                    addAuctionSiteDLVs({
                        listingId: auctionBiddingData?.ListingID,
                        eventId: eventID,
                        eventName: eventTitle, 
                        sellerId: props.SellerCRMID,
                        sellerName: props.dealerName,
                        userID: currentUser.CRMContactID,
                        gtmEvent: bidType,
                        bidAmount: bidValue,
                        bidValue: value 
                    });
                }
                else if (data.IsPaused) {
                    // Force a reset from the server if the bid result comes back as paused
                    resetAuctionData?.(auctionBiddingData?.ListingID, data.AddedToWatchlist, null);
                }
                else if (props.IsAT && data.IsInvalidAutoBidIncrement) {
                    showInvalidAutoBidIncrementError();
                }
                else {
                    if (data?.BidAgreementCheckFailed == true) {
                        addUpdateBAAcceptanceToSessionStorage(currentUser?.UserAuthID, biddingAgreement?.BidAgreement?.EventOrSaleID, false);                        

                        let updatedBiddingAgreement = {
                            ...biddingAgreement, BidAgreement: { ...biddingAgreement.BidAgreement, UserIsApproved: false, UserAccepted: false }
                        };
                        onBiddingAgreementDataUpdated(updatedBiddingAgreement);
                    }
                    setBidErrorDetails({
                        ...bidErrorDetails, errorMessage: data.Message
                    });                    
                }
            })
                .catch((error) => {
                    //setBidError("Failed to submit bid, please refresh and try again.");
                    setBidErrorDetails({
                        ...bidErrorDetails, errorMessage: 'Failed to submit bid, please refresh and try again.'
                    });
                    setIsLoading(false);
                });
        }
    };

    const onMultiFactorAuthorized = () => {
        biddingAgreement.HasPassedMultiFactorAuthentication = true;
        onBiddingAgreementDataUpdated(biddingAgreement);
        setShowMFAForm(false);
        acceptAgreement();
    };

    const firstTimeAcceptingAgreementOnDetailsPage = () => {
        // If this is the details page, we want to make sure we are hiding the agreement after it's accepted to let them enter a bid
        return firstTimeUserAcceptedAgreement && props.IsDetailPage;
    }

    const renderAuthenticatedAcceptanceBtns = () => {
        if (props.IsAT && currentUser?.BidderStatus != bidderStatus.ACTIVE) {
            return (
                <>
                    <a href={routes.CanIBid}>
                        <Button variant="contained">{translator.translate('sandhills.tradepubwebsite.mvc.canibid')}</Button>
                    </a>
                    {renderCancelBtn()}
                </>
            );
        }
        else if (isLargeBid && !isLargeBidConfirmed && biddingAgreement?.BidAgreement && biddingAgreement?.BidAgreement?.UserAccepted) {
            return (
                <>
                    {((props.IsAT && firstTimeAcceptingAgreementOnDetailsPage()) || (!props.IsAT && firstTimeUserAcceptedAgreement)) ?
                        autoCloseModal()
                        : (
                            <>
                                <Button
                                    variant="contained" onClick={() => {
                                        setIsLargeBidConfirmed(true);
                                        if (currentSite?.Site?.IsAuctionTime) {
                                            sandhillsLogging(routes.LogBidInformation, {
                                                listingId: auctionBiddingData.ListingID,
                                                bidValue: bidValue,
                                                logLocation: 'largeBidModalConfirmed'
                                            });
                                        }
                                    }} disabled={!largeBidConfirmChecked}>{translator.translate('sandhills.tradepubwebsite.mvc.submit')}</Button>
                                {renderCancelBtn()}
                            </>
                        )
                    }
                </>
            );
        }
        else if (biddingAgreement?.BidAgreement?.UserIsApproved && auctionBiddingData && !firstTimeAcceptingAgreementOnDetailsPage()) {
            //EF pre-bidding or AT max bid entry
            return (
                <>
                    {(!bidConfirmed && !bidErrorDetails.errorMessage) ? 
                        (
                            <>
                                <Button variant="contained" onClick={submitBid}>{translator.translate('sandhills.confirm')}</Button>
                                {renderCancelBtn()}
                            </>
                        ) : null
                    } 
                </>
            );
        }
        else {
            if (biddingAgreement.BidAgreement.UserAccepted) {
                return (
                    <>
                        <div className="terms-accepted"><p>{translator.translate('sandhills.tradepubwebsite.mvc.termsandconditionsaccepted')}</p></div>
                        {(biddingAgreement.PaymentGateways && biddingAgreement.PaymentGateways.length > 0 && !biddingAgreement.BidAgreement.UserIsApproved && biddingAgreement.BidAgreement.VettingBidderStatus != bidderStatus.REVOKED) ?
                            <>
                                <Button className='payment-auth-btn' variant="contained" onClick={() => setShowPaymentAuthForm(true)}>{translator.translate('sandhills.paymentauthorization')}</Button>
                            </>
                            : firstTimeUserAcceptedAgreement && biddingAgreement.BidAgreement.UserIsApproved ?
                            // Only automatically close after user accepts T&C for the first time && CC/MFA auth isn't needed
                            <>
                                {autoCloseModal()}
                            </>
                            : null
                        }
                    </>
                );
            }
            else if (!biddingAgreement.BidAgreement.BidAgreementAcceptanceRequired) {
                return (
                    <>
                        <div className="terms-accepted"></div>                        
                    </>
                );
            }
            else {
                if (currentUser.BidderStatus && currentUser.BidderStatus == bidderStatus.ACTIVE) {
                    return (
                        <>
                            <Button variant="contained" onClick={acceptAgreement}>{translator.translate('sandhills.accepttermsandconditions')}</Button>
                            {renderCancelBtn()}
                        </>
                    );
                }
                else {
                    return (
                        <>
                            <a href={biddingAgreement.CanIBidUrl}>
                                <Button variant="contained">{translator.translate('sandhills.tradepubwebsite.mvc.canibid')}</Button>
                            </a>
                            {renderCancelBtn()}
                        </>
                    );
                }
            }
        }
    };


    const renderCancelBtn = (displayText) => {
        return (
            <Button className="agreement-cancel" color='cancel' variant="outlined" onClick={onClose}>{displayText || translator.translate('sandhills.cancel')}</Button>
        );
    };

    const getHeader = () => {
        if (showPaymentAuthForm) {
            return (<h3>{translator.translate('sandhills.paymentauthorization').toUpperCase()}</h3>);
        }
        else if (biddingAgreement?.BidAgreement?.UserIsApproved && auctionBiddingData && !firstTimeAcceptingAgreementOnDetailsPage()) {
            if (isLargeBid && !isLargeBidConfirmed) {
                return <></>
            }
            else if (bidConfirmed) {
                return (<h3>{translator.translate('sandhills.tradepubwebsite.mvc.bidconfirmed')}</h3>);
            }
            else {
                return (<h3>{translator.translate('sandhills.tradepubwebsite.mvc.confirmyourbid')}</h3>);
            }
        }
        else if (biddingAgreement?.BidAgreement?.EventCategoryID && biddingAgreement?.BidAgreement?.EventCategoryID != eventCategory.onSiteAuction) {
            return (<h3>{translator.translate('sandhills.auctioneerordealertermsandconditions').toUpperCase()}</h3>);
        }
        else{
            return (<h3>{translator.translate('sandhills.termsandconditions').toUpperCase()}</h3>);
        }
    };

    const getBody = () => {
        if (showMFAForm) {
            return (
                <div>
                    <MultiFactorAuthenticationForm
                        forceNewMFA={false}
                        MultifactorAuthenticationValues={biddingAgreement.MultifactorAuthenticationValues}
                        onMultiFactorAuthorized={onMultiFactorAuthorized}
                        onMultiFactorCanceled={() => setShowMFAForm(false)} />
                </div>
            );
        }
        else if (showPaymentAuthForm) {
            return (
                <PaymentAuthorizationForm
                    {...biddingAgreement}
                    autoCloseModal={auctionBiddingData ? null : autoCloseModal}
                    biddingStatus={currentUser.BidderStatus}
                    onBiddingAgreementDataUpdated={onBiddingAgreementDataUpdated} />
            );
        }
        else if (bidErrorDetails?.errorMessage?.length > 0 || (biddingAgreement?.BidAgreement?.UserIsApproved && auctionBiddingData && !firstTimeAcceptingAgreementOnDetailsPage())) {
            return (
                <BiddingConfirmation
                    {...auctionBiddingData}                    
                    ErrorDetails={bidErrorDetails}
                    BidConfirmed={bidConfirmed}
                    AutoOutbid={autoOutbid}
                    TieBid={tieBid}
                    notificationSettings={notificationSettings}
                    WatchlistUrl={props.WatchlistUrl}
                    onClose={closeModal}
                    showCurrencyPopUp={showCurrencyPopUp}
                    BidPopUpURL={props.BidPopUpURL}
                    biddingAgreement={biddingAgreement}
                    IsAT={props.IsAT}
                    confirmLargeBid={bidErrorDetails.errorMessage == '' && isLargeBid && !isLargeBidConfirmed}
                    largeBidConfirmChecked={largeBidConfirmChecked}
                    setLargeBidConfirmChecked={setLargeBidConfirmChecked}
                />
            );
        }
        else {
            return (
                <div dangerouslySetInnerHTML={{ __html: biddingAgreement.BidAgreement.AgreementText }}></div>
            );
        }
    };

    const getFooter = () => {
        return (
            <div className='accept-buttons'>
                {isLoading && <LoadingRing />}
                {currentUser.IsAuthenticated ?
                    <>
                        {renderAuthenticatedAcceptanceBtns()}
                    </>
                    :
                    <>
                        <a href={loginURL}>
                            <Button variant="contained">{translator.translate('sandhills.bidcallerboard.mvc.loginorregister')}</Button>
                        </a>
                        {renderCancelBtn()}
                    </>
                }
            </div>
        );
    };

    return (
        <Modal containerClassName={'bidding-agreement-modal' + (showPaymentAuthForm ? ' cc-auth' : '') + (showMFAForm ? ' bidding-agreement-modal-mfa' : '')} onEscapeKeyClose={closeModal}>
            <>
                {auctionBiddingData?.ListingID &&
                    <input type="hidden" id="gtm-listingID" value={auctionBiddingData.ListingID} />}
            </>
            <Modal.Header onClose={closeModal}>
                {!showMFAForm &&
                    getHeader()}
            </Modal.Header>

            <Modal.Body bodyClassName={props?.biddingAgreement?.BidAgreement?.UserAccepted ? 'ts-modal-body-text-align-center' : 'ts-modal-body-text-align-left'}>
                <p id='description-label' className='hidden'>Bidding Agreement Form.</p>
                {getBody()}
            </Modal.Body>
            {!showPaymentAuthForm && !showMFAForm && (!bidErrorDetails || bidErrorDetails.errorMessage == '') &&
                <Modal.Footer>
                    {getFooter()}
                </Modal.Footer>}
        </Modal>
    );
}

export default BiddingAgreementModal;